import React, { useState, useMemo } from 'react';
import { format, eachDayOfInterval, parseISO, isBefore, startOfDay, addDays } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

/**
 * BookingDatePicker - A calendar-based date picker that greys out booked dates.
 *
 * Props:
 * - value: string (YYYY-MM-DD) - currently selected date
 * - onChange: (dateString: string) => void
 * - bookedRanges: Array<{ check_in: string, check_out: string }> - booked date ranges
 * - minDate: Date - earliest selectable date (default: today)
 * - label: string - label for the input
 * - placeholder: string
 * - disabled: boolean
 * - testId: string - data-testid attribute
 */
const BookingDatePicker = ({
  value,
  onChange,
  bookedRanges = [],
  minDate,
  label,
  placeholder = 'Select date',
  disabled = false,
  testId,
}) => {
  const [open, setOpen] = useState(false);

  const today = startOfDay(new Date());
  const effectiveMinDate = minDate ? startOfDay(minDate) : today;

  // Build a set of all disabled dates from booked ranges
  const disabledDates = useMemo(() => {
    const dates = [];
    for (const range of bookedRanges) {
      try {
        const start = parseISO(range.check_in);
        // check_out day itself is not occupied (guest checks out that morning)
        // so we disable from check_in to the day BEFORE check_out
        const end = parseISO(range.check_out);
        if (start < end) {
          const interval = eachDayOfInterval({
            start,
            end: addDays(end, -1), // exclude checkout day
          });
          dates.push(...interval);
        }
      } catch {
        // skip malformed ranges
      }
    }
    return dates;
  }, [bookedRanges]);

  // Matcher function: disable dates before min and booked dates
  const disabledMatcher = useMemo(() => {
    const matchers = [];

    // Disable dates before the minimum date
    if (effectiveMinDate) {
      matchers.push({ before: effectiveMinDate });
    }

    // Disable individual booked dates
    if (disabledDates.length > 0) {
      matchers.push(...disabledDates);
    }

    return matchers;
  }, [effectiveMinDate, disabledDates]);

  const selectedDate = value ? parseISO(value) : undefined;

  const handleSelect = (date) => {
    if (date) {
      onChange(format(date, 'yyyy-MM-dd'));
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            'w-full justify-start text-left font-normal',
            !value && 'text-muted-foreground'
          )}
          style={{
            borderRadius: 0,
            height: '40px',
            backgroundColor: 'var(--bg-primary, #fff)',
            borderColor: 'var(--border-color, #e5e7eb)',
            color: value ? 'var(--text-primary, #1a1a1a)' : 'var(--text-secondary, #6b7280)',
          }}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? format(parseISO(value), 'PPP') : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start"
        style={{
          backgroundColor: 'var(--bg-primary, #fff)',
          zIndex: 50,
        }}
      >
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleSelect}
          disabled={disabledMatcher}
          defaultMonth={selectedDate || effectiveMinDate}
          autoFocus
          modifiers={{
            booked: disabledDates,
          }}
          modifiersStyles={{
            booked: {
              backgroundColor: 'var(--bg-booked, #f3f4f6)',
              color: 'var(--text-disabled, #9ca3af)',
              textDecoration: 'line-through',
              opacity: 0.5,
              cursor: 'not-allowed',
            },
          }}
        />
        {bookedRanges.length > 0 && (
          <div
            className="px-3 pb-3 pt-1 text-xs flex items-center gap-1.5"
            style={{ color: 'var(--text-secondary, #6b7280)' }}
          >
            <span
              className="inline-block w-3 h-3 rounded-sm"
              style={{
                backgroundColor: 'var(--bg-booked, #f3f4f6)',
                border: '1px solid var(--border-color, #e5e7eb)',
              }}
            />
            <span style={{ textDecoration: 'line-through', opacity: 0.5 }}>Dates</span>
            {' '}= Already booked
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default BookingDatePicker;
